// Cliente de "crear evento con IA". Con VITE_API_URL habla con la API (rutas asíncronas de apps/api/src/routes/ai.ts);
// sin ella simula la generación por secciones con un borrador de ejemplo, para desarrollar la pantalla sin modelo.
import type { EventPackage, Section } from '@cuorum/contracts/ai-package';
import { SECTIONS } from '@cuorum/contracts/ai-package';

export interface GenerationDto {
  id: string;
  status: 'running' | 'done' | 'failed';
  sections: Section[];
  result: EventPackage | null;
  error: { code: string; message: string } | null;
}

export interface StartInput { description: string; eventType: string | null }

export interface AiApi {
  readonly mode: 'http' | 'demo';
  start(input: StartInput): Promise<{ id: string }>;
  get(id: string, signal?: AbortSignal): Promise<GenerationDto>;
}

export class AiApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) { super(message); this.name = 'AiApiError'; this.status = status; }
}

export function createAiApi(baseUrl: string | undefined, accountId: string): AiApi {
  return baseUrl ? httpAiApi(baseUrl, accountId) : demoAiApi();
}

function httpAiApi(baseUrl: string, accountId: string): AiApi {
  const root = `${baseUrl.replace(/\/$/, '')}/v1/accounts/${encodeURIComponent(accountId)}/ai/generations`;
  const parse = async <T,>(res: Response): Promise<T> => {
    if (res.ok) return (await res.json()) as T;
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new AiApiError(res.status, body.message ?? `HTTP ${res.status}`);
  };
  return {
    mode: 'http',
    async start(input) {
      return parse(await fetch(root, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) }));
    },
    async get(id, signal) {
      return parse(await fetch(`${root}/${encodeURIComponent(id)}`, { credentials: 'include', signal }));
    },
  };
}

// ───────────────────────── modo demostración ─────────────────────────

const SECTION_MS = 650;

function demoAiApi(): AiApi {
  const runs = new Map<string, { startedAt: number; input: StartInput }>();
  return {
    mode: 'demo',
    async start(input) {
      const id = crypto.randomUUID();
      runs.set(id, { startedAt: Date.now(), input });
      return { id };
    },
    async get(id) {
      const run = runs.get(id);
      if (!run) throw new AiApiError(404, 'no encontrado');
      const n = Math.min(SECTIONS.length, Math.floor((Date.now() - run.startedAt) / SECTION_MS));
      const done = n >= SECTIONS.length;
      return { id, status: done ? 'done' : 'running', sections: SECTIONS.slice(0, n), result: done ? demoPackage(run.input) : null, error: null };
    },
  };
}

/** Borrador de ejemplo armado con la primera frase del texto. No inventa fecha ni dirección: las deja por confirmar. */
export function demoPackage({ description, eventType }: StartInput): EventPackage {
  const first = description.split(/(?<=[.!?])\s/)[0]?.trim() ?? description;
  const title = first.length > 80 ? `${first.slice(0, 77).trimEnd()}…` : first.replace(/[.!?]$/, '');
  const isConsulta = /consulta|vecin|barrio|municip|plan/i.test(description);
  return {
    event: {
      title: title.charAt(0).toUpperCase() + title.slice(1),
      kicker: eventType ?? (isConsulta ? 'Encuentro ciudadano' : null),
      summary: description.slice(0, 190),
      about: [description.slice(0, 650)],
      type: isConsulta ? 'consulta' : 'taller',
      startsAt: null,
      endsAt: null,
      timeZone: 'America/Santiago',
      venue: { name: null, address: null },
      capacity: null,
      ...extractCapacity(description),
    },
    agenda: [],
    registration: { question: isConsulta ? { label: '¿Desde qué barrio vienes?', options: ['Centro', 'Norte', 'Sur', 'Otro'] } : null },
    emails: {
      confirmation: { subject: 'Tu entrada está lista', intro: '¡Gracias por inscribirte! Te esperamos.' },
      reminder: { subject: 'Mañana es el evento', intro: 'Te recordamos que mañana nos vemos. Llega 10 minutos antes.' },
    },
    live: { items: [{ kind: 'poll', prompt: '¿Qué tema deberíamos priorizar?', options: ['Opción A', 'Opción B', 'Opción C'] }, { kind: 'questions', prompt: 'Preguntas al panel' }] },
    style: { suggested: isConsulta ? 'institucional' : 'limpio', reason: isConsulta ? 'Es un encuentro ciudadano: el estilo institucional da confianza.' : 'Sirve para cualquier evento.' },
    review: [
      { field: 'event.startsAt', note: '¿Qué día y a qué hora es? Sin fecha no podemos abrir la inscripción.' },
      { field: 'event.venue.name', note: '¿Dónde será? Agrega el lugar y la dirección.' },
    ],
  };
}

function extractCapacity(text: string): { capacity: number } | Record<string, never> {
  const m = /(\d{2,5})\s*(personas|asistentes|cupos|vecinos)/i.exec(text);
  return m ? { capacity: Number(m[1]) } : {};
}
